import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { adminRequest, AdminApiError } from './admin-api';
import { resources } from '../../app/src/i18n/resources';
type Run = {
  id: string;
  status: string;
  pages: number;
  records: number;
  changed: number;
  added?: number;
  updated?: number;
  legacy_changed?: number;
  delta_records?: number | null;
  delta_added?: number | null;
  delta_updated?: number | null;
  delta_retired?: number | null;
  retired: number;
  heartbeat_at: string;
  error_code: string | null;
  error_record_key?: string | null;
};
export type SyncEvent = Run & {
  run_id: string;
  created_at: string;
  kind: string;
  window_from: string;
  window_until: string;
};
type DisplayEvent = SyncEvent & { minuteStart?: string; checkpointCount?: number };

// Group only progress; lifecycle/error entries retain their own identity and time.
// The endpoint is bounded, so these are sums of available checkpoints, not a
// claim that every minute is complete. Never infer deltas from cumulative totals.
export function groupProgressEvents(events: SyncEvent[]): DisplayEvent[] {
  const result: DisplayEvent[] = [];
  const groups = new Map<string, DisplayEvent>();
  for (const event of [...events].sort(
    (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
  )) {
    const minute = Math.floor(Date.parse(event.created_at) / 60000) * 60000;
    if (event.kind !== 'progress' || event.error_code || !Number.isFinite(minute)) {
      result.push({ ...event });
      continue;
    }
    const key = JSON.stringify([event.run_id, event.window_from, event.window_until, minute]);
    const existing = groups.get(key);
    if (!existing) {
      const group = { ...event, minuteStart: new Date(minute).toISOString(), checkpointCount: 1 };
      groups.set(key, group);
      result.push(group);
    } else {
      existing.checkpointCount! += 1;
      for (const field of [
        'delta_records',
        'delta_added',
        'delta_updated',
        'delta_retired',
      ] as const) {
        existing[field] =
          existing[field] != null && event[field] != null ? existing[field]! + event[field]! : null;
      }
    }
  }
  return result;
}
export type Status = {
  throughput?: {
    windowMinutes: number;
    pagesPerMinute: number;
    recordsPerMinute: number;
    backlogHours: number | null;
  };
  health: string;
  baselineVerified: boolean;
  lagHours: number | null;
  state: { completed_until: string | null } | null;
  runs: Run[];
  events?: SyncEvent[];
};
const copy = resources.en.translation.catalogueSync;
const statusLabel = (value: string) => copy.statuses[value as keyof typeof copy.statuses] ?? value;
export function CatalogueSyncPanel({
  getAccessToken,
  onAccessDenied,
}: {
  getAccessToken: () => Promise<string>;
  onAccessDenied: () => void;
}) {
  const [data, setData] = useState<Status | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const value = (await adminRequest(getAccessToken, '/admin/operations/catalogue-sync', {
        signal: AbortSignal.timeout(8000),
      })) as Status;
      if (!value || !Array.isArray(value.runs) || typeof value.health !== 'string')
        throw new Error('Invalid response');
      setData(value);
    } catch (e) {
      setError(true);
      if (e instanceof AdminApiError && (e.status === 401 || e.status === 403)) onAccessDenied();
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, onAccessDenied]);
  useEffect(() => {
    void load();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 60000);
    return () => clearInterval(timer);
  }, [load]);
  return (
    <CatalogueSyncView data={data} error={error} loading={loading} refresh={() => void load()} />
  );
}

export function CatalogueSyncView({
  data,
  error,
  loading,
  refresh,
}: {
  data: Status | null;
  error: boolean;
  loading: boolean;
  refresh: () => void;
}) {
  const [problemsOnly, setProblemsOnly] = useState(false);
  return (
    <section className="admin-panel mb-8 p-5 text-ink sm:p-6" aria-label={copy.title}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl font-medium">{copy.title}</h2>
        <button type="button" onClick={refresh} disabled={loading} className="admin-button">
          {copy.refresh}
        </button>
      </div>
      <p className="mt-2 text-sm text-ink-soft">{copy.schedule}</p>
      <div aria-live="polite">
        {error ? (
          <p role="alert" className="mt-3 text-danger">
            {copy.unavailable}
          </p>
        ) : null}
        {!data && !error ? (
          <p>{copy.loading}</p>
        ) : data ? (
          <>
            <p className="mt-3">
              {copy.state}:{' '}
              <strong>
                {statusLabel(
                  data.health !== 'failed' && data.runs[0]?.status === 'running'
                    ? 'running'
                    : data.health,
                )}
              </strong>{' '}
              · {copy.lag}: {data.lagHours === null ? '—' : `${data.lagHours.toFixed(1)} h`}
            </p>
            <p>
              {copy.completed}:{' '}
              {data.state?.completed_until
                ? new Date(data.state.completed_until).toLocaleString()
                : copy.none}
            </p>
            {data.throughput && (
              <div className="my-4 rounded-xl border border-line/60 bg-panel-raised p-4 text-sm leading-7">
                <p>
                  {copy.throughput}: <strong>{data.throughput.recordsPerMinute.toFixed(1)}</strong>{' '}
                  {copy.recordsPerMinute} ·{' '}
                  <strong>{data.throughput.pagesPerMinute.toFixed(1)}</strong> {copy.pagesPerMinute}
                </p>
                <p>
                  {copy.backlog}:{' '}
                  {data.throughput.backlogHours === null
                    ? '—'
                    : `${data.throughput.backlogHours.toFixed(1)} h`}
                </p>
                <p className="mt-1 text-xs text-ink-soft">{copy.throughputNote}</p>
              </div>
            )}
            {!data.baselineVerified && (
              <p className="my-3 rounded-lg bg-brass/10 p-3 text-sm text-ink">{copy.baseline}</p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr>
                    {[
                      copy.run,
                      copy.state,
                      copy.pages,
                      copy.records,
                      copy.added,
                      copy.updated,
                      copy.legacyChanged,
                      copy.retired,
                      copy.heartbeat,
                      copy.error,
                    ].map((s) => (
                      <th className="p-2" key={s}>
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.runs.map((r) => (
                    <tr key={r.id}>
                      {[
                        r.id,
                        statusLabel(r.status),
                        r.pages,
                        r.records,
                        r.added ?? '—',
                        r.updated ?? '—',
                        r.legacy_changed ?? r.changed,
                        r.retired,
                        new Date(r.heartbeat_at).toLocaleString(),
                        r.error_code ?? '—',
                      ].map((v, i) => (
                        <td className="border-t border-line/30 p-2" key={i}>
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 border-t border-line/30 pt-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold">{copy.eventsTitle}</h3>
                <button
                  type="button"
                  aria-pressed={problemsOnly}
                  onClick={() => setProblemsOnly(!problemsOnly)}
                  className="admin-button aria-pressed:bg-thread-soft"
                >
                  {problemsOnly ? copy.allEvents : copy.problems}
                </button>
              </div>
              <p className="my-3 text-sm text-ink-soft">
                {copy.eventsNote} {copy.splitNote}
              </p>
              <ol className="max-h-96 overflow-y-auto space-y-3" aria-label={copy.eventsTitle}>
                {groupProgressEvents(data.events ?? [])
                  .filter(
                    (e) =>
                      !problemsOnly ||
                      e.kind === 'failed' ||
                      e.kind === 'retry' ||
                      e.kind === 'window_reduced' ||
                      e.error_code,
                  )
                  .map((e) => (
                    <li
                      key={e.id}
                      className="rounded-lg border border-line/40 bg-panel-raised p-3 text-sm"
                    >
                      <div className="flex flex-wrap justify-between gap-2">
                        <strong
                          className={
                            e.error_code || e.kind === 'failed' ? 'text-danger' : 'text-thread'
                          }
                        >
                          {e.minuteStart
                            ? copy.minuteProgress
                            : (copy.events[e.kind as keyof typeof copy.events] ??
                              statusLabel(e.kind))}
                        </strong>
                        <time dateTime={e.created_at} className="text-ink-soft">
                          {e.minuteStart
                            ? `${new Date(e.minuteStart).toLocaleString()} – ${new Date(Date.parse(e.minuteStart) + 60000).toLocaleTimeString()}`
                            : new Date(e.created_at).toLocaleString()}
                        </time>
                      </div>
                      {e.minuteStart && (
                        <p className="mt-2 text-xs text-ink-soft">
                          {copy.checkpointsIncluded}: {e.checkpointCount}
                        </p>
                      )}
                      {e.delta_records != null &&
                      e.delta_added != null &&
                      e.delta_updated != null &&
                      e.delta_retired != null ? (
                        <>
                          <p className="mt-3 text-xs text-ink-soft">
                            {e.minuteStart ? copy.intervalIncrements : copy.thisCheckpoint} ·{' '}
                            {copy.run} {e.run_id} · {copy.pages}: {e.pages.toLocaleString()}
                          </p>
                          <dl className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {[
                              [copy.records, e.delta_records, e.records],
                              [copy.added, e.delta_added, e.added],
                              [copy.updated, e.delta_updated, e.updated],
                              [copy.retired, e.delta_retired, e.retired],
                            ].map(([label, delta, total]) => (
                              <div key={String(label)}>
                                <dt className="text-xs text-ink-soft">{label}</dt>
                                <dd>
                                  <strong className="text-lg text-ink">
                                    {Number(delta) > 0 ? '+' : ''}
                                    {Number(delta).toLocaleString()}
                                  </strong>
                                  <span className="block text-xs text-ink-soft">
                                    {copy.total}:{' '}
                                    {typeof total === 'number' ? total.toLocaleString() : '—'}
                                  </span>
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </>
                      ) : (
                        <p className="mt-1">
                          {copy.totalsOnly} · {copy.run} {e.run_id} · {copy.pages}:{' '}
                          {e.pages.toLocaleString()} · {copy.records}: {e.records.toLocaleString()}{' '}
                          · {copy.added}: {e.added ?? '—'} · {copy.updated}: {e.updated ?? '—'} ·{' '}
                          {copy.legacyChanged}: {e.legacy_changed ?? e.changed} · {copy.retired}:{' '}
                          {e.retired.toLocaleString()}
                        </p>
                      )}
                      <p className="mt-1 text-ink-soft">
                        {copy.window}: {new Date(e.window_from).toLocaleString()} —{' '}
                        {new Date(e.window_until).toLocaleString()}
                      </p>
                      {e.error_code && (
                        <p className="mt-2 text-danger">
                          {copy.error}: {copy.errors[e.error_code as keyof typeof copy.errors]}{' '}
                          <code>{e.error_code}</code>{' '}
                          {e.error_record_key && <code>({e.error_record_key})</code>}
                        </p>
                      )}
                    </li>
                  ))}
                {!(data.events ?? []).some(
                  (e) =>
                    !problemsOnly ||
                    e.kind === 'failed' ||
                    e.kind === 'retry' ||
                    e.kind === 'window_reduced' ||
                    e.error_code,
                ) && <li className="text-ink-soft">{copy.noEvents}</li>}
              </ol>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
